import { componentSizes } from '@/theme/componentSizes';
import { resolveSemanticColor } from '@/theme/semanticTokens';
import { s, typographyTokens } from '@/theme/tokens';
import { alpha } from '@/ui/primitives/colorUtils';
import type { SxProps, Theme } from '@/ui/primitives';

type ThemeSxObject = Exclude<SxProps<Theme>, readonly unknown[] | ((theme: Theme) => unknown)>;
type ThemeSxFn = (theme: Theme) => ThemeSxObject;

export const topBarContextProjectMenuItemHeight = 38;
export const topBarContextPopoverMaxWidth = 360;
export const topBarContextPopoverCompactMaxWidth = 'calc(100vw - 32px)';
export const topBarContextButtonHeight = componentSizes.filters.toolbarControlHeight;
export const topBarContextChevronSize = 13;
export const topBarContextProjectChevronSize = 20;
export const topBarContextButtonGap = s('sm');
export const topBarContextProjectButtonGap = s('sm');

export const topBarContextIconSx = {
  fontSize: 18,
  stroke: 'currentColor',
  strokeWidth: 0.08,
} as const;

export const topBarContextProjectIconSx = {
  fontSize: 17,
  stroke: 'currentColor',
  strokeWidth: 0.08,
} as const;

type BuildTopBarContextButtonSxArgs = {
  disabled: boolean;
  gap: string;
  useCompactContextControl: boolean;
};

export const buildTopBarContextButtonSx =
  ({ disabled, gap, useCompactContextControl }: BuildTopBarContextButtonSxArgs): ThemeSxFn =>
  (theme) => ({
    appearance: 'none',
    border: 'none',
    backgroundColor: 'transparent',
    color: theme.app.colors.textMuted,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: useCompactContextControl ? 'center' : 'flex-start',
    gap,
    height: topBarContextButtonHeight,
    minWidth: 0,
    width: 'auto',
    maxWidth: 260,
    px: useCompactContextControl ? s('xs') : s('sm'),
    borderRadius: s('sm'),
    cursor: disabled ? 'default' : 'pointer',
    font: 'inherit',
    flexShrink: 1,
    transition: 'background-color 120ms ease, color 120ms ease',
    '&:hover': {
      backgroundColor: disabled ? 'transparent' : resolveSemanticColor(theme, 'surfaceHover'),
      color: disabled ? theme.app.colors.textMuted : theme.app.colors.text,
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.app.state.focus}`,
      outlineOffset: 2,
    },
  });

export const buildTopBarContextButtonIconSlotSx =
  (hasIcon: boolean): ThemeSxFn =>
  (theme) => ({
    color: theme.app.colors.textMuted,
    display: hasIcon ? 'inline-flex' : 'none',
    alignItems: 'center',
    height: 20,
    flexShrink: 0,
  });

export const topBarContextButtonLabelSx = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block',
  lineHeight: 1,
  fontSize: 14,
  fontWeight: typographyTokens.bodyMedium.fontWeight,
  color: (theme: Theme) => theme.app.colors.textMuted,
} as const;

export const buildTopBarContextChevronSx = (chevronSize: number): ThemeSxObject => ({
  fontSize: chevronSize,
  flexShrink: 0,
  color: (theme: Theme) => theme.app.colors.textMuted,
  ml: s('none'),
  opacity: 0.74,
});

export const topBarContextRootSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: s('xs'),
  minWidth: 0,
  maxWidth: '100%',
  width: '100%',
} as const;

export const buildTopBarContextRootSx = (useCompactContextControl: boolean): ThemeSxObject => ({
  ...topBarContextRootSx,
  justifyContent: useCompactContextControl ? 'center' : 'flex-start',
});

export const topBarContextDividerTextSx = {
  fontSize: 14,
  color: (theme: Theme) => theme.app.colors.textMuted,
  mx: s('none'),
  flexShrink: 0,
  opacity: 0.56,
} as const;

export const topBarContextOrganisationMenuTextSx = {
  minWidth: 0,
  overflow: 'hidden',
} as const;

export const topBarContextAddProjectActionSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: s('sm'),
} as const;

export const topBarContextProjectPrimaryTextSx = {
  display: 'block',
  fontSize: '14px !important',
  fontWeight: typographyTokens.overline.fontWeight,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

type BuildTopBarContextPopoverPaperSxArgs = {
  borderColor: string;
  surfaceColor: string;
};

export const buildTopBarContextPopoverPaperSx = ({
  borderColor,
  surfaceColor,
}: BuildTopBarContextPopoverPaperSxArgs): ThemeSxObject => ({
  mt: s('xs'),
  width: topBarContextPopoverCompactMaxWidth,
  minWidth: 0,
  maxWidth: topBarContextPopoverCompactMaxWidth,
  border: `1px solid ${borderColor}`,
  borderRadius: '4px !important',
  backgroundColor: surfaceColor,
  backgroundImage: 'none',
  boxShadow: 'none',
  overflow: 'hidden',
  [`@media (min-width:${String(topBarContextPopoverMaxWidth + 64)}px)`]: {
    width: 'max-content',
    maxWidth: topBarContextPopoverMaxWidth,
  },
});

export const topBarContextPopoverPaperSx: ThemeSxFn = (theme) =>
  buildTopBarContextPopoverPaperSx({
    borderColor: theme.app.colors.border,
    surfaceColor: theme.app.colors.surface,
  });

export const topBarContextOrganisationMenuListSx = {
  maxWidth: 'inherit',
  '& .MuiMenuItem-root': {
    minHeight: `${String(topBarContextProjectMenuItemHeight)}px`,
    px: s('md'),
    py: s('sm'),
    gap: s('sm'),
    minWidth: 0,
  },
  '& .MuiMenuItem-root.Mui-selected': {
    backgroundColor: (theme: Theme) => alpha(theme.app.colors.primary, 0.1),
    color: (theme: Theme) => theme.app.colors.text,
  },
  '& .MuiMenuItem-root.Mui-selected:hover, & .MuiMenuItem-root.Mui-selected.Mui-focusVisible': {
    backgroundColor: (theme: Theme) => alpha(theme.app.colors.primary, 0.14),
  },
  '& .MuiListItemIcon-root': {
    minWidth: 20,
    flexShrink: 0,
    color: 'inherit',
  },
  '& .MuiListItemText-primary': {
    color: 'inherit',
    fontWeight: typographyTokens.overline.fontWeight,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
} as const;

export const topBarContextProjectMenuListSx = {
  py: s('none'),
  maxWidth: 'inherit',
  '& .MuiMenuItem-root': {
    minHeight: `${String(topBarContextProjectMenuItemHeight)}px`,
    px: s('md'),
    py: s('sm'),
    gap: s('md'),
    minWidth: 0,
  },
  '& .MuiMenuItem-root.Mui-selected': {
    backgroundColor: (theme: Theme) => alpha(theme.app.colors.primary, 0.1),
    color: (theme: Theme) => theme.app.colors.text,
  },
  '& .MuiMenuItem-root.Mui-selected:hover, & .MuiMenuItem-root.Mui-selected.Mui-focusVisible': {
    backgroundColor: (theme: Theme) => alpha(theme.app.colors.primary, 0.14),
  },
  '& .MuiListItemText-primary': {
    ...topBarContextProjectPrimaryTextSx,
    color: 'inherit',
  },
  '& .MuiListItemText-secondary': {
    fontWeight: typographyTokens.body.fontWeight,
  },
  '& .MuiDivider-root': {
    my: `${s('none')} !important`,
    marginTop: `${s('none')} !important`,
    marginBottom: `${s('none')} !important`,
  },
} as const;
